import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Form, Input, Select, Radio, Switch, InputNumber, Button, Space, Typography, Row, Col, Divider, Flex, Avatar, Tooltip, message, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { useList } from "@refinedev/core";
import { ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, SaveOutlined, EditOutlined, UserOutlined, LeftOutlined, UndoOutlined } from "@ant-design/icons";
import { AssignmentModal } from "../../orders/components/AssignmentModal";
import { TunerRole } from "../../../models/user";
import { getAvatarColor, getInitials, getInitialsFromName } from "../../../shared/utils";
import { useAvatarPalette, usePageStyles } from "../../../shared/styles";
import { ThunderboltOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const UNGROUPED_KEY = "__ungrouped__";

interface AssignmentRule {
    id: string;
    name: string;
    description?: string;
    assignmentType: "user" | "service_provider" | "group";
    targetUserId?: number;
    targetServiceProviderId?: number;
    targetGroupId?: number;
    strategy: "immediate" | "delayed";
    delayMinutes?: number;
    productIds?: string[] | number[]; // May come from API as array of IDs
    products?: Array<{ id: number | string;[key: string]: any }>; // API returns full product objects
    accountCondition: "all_accounts" | "specific_accounts";
    accountIds?: string[] | number[]; // May come from API as array of IDs
    accounts?: Array<{ id: number | string;[key: string]: any }>; // API returns full account objects
    priority: number;
    isActive: boolean;
    _isNew?: boolean;
}

interface AssignmentRuleCardProps {
    rule: AssignmentRule;
    index: number;
    totalRules: number;
    onUpdate: (id: string | number, values: Partial<AssignmentRule>) => void;
    onDelete: (id: string | number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    onLocalUpdate?: (id: string, values: Partial<AssignmentRule>) => void;
    usersData?: any;
    groupsData?: any;
    serviceProvidersData?: any;
}

export const AssignmentRuleCard: React.FC<AssignmentRuleCardProps> = ({
    rule,
    index,
    totalRules,
    onUpdate,
    onDelete,
    onMoveUp,
    onMoveDown,
    onLocalUpdate,
    usersData,
    groupsData,
    serviceProvidersData,
}) => {
    const _t = useTranslate();
    const [form] = Form.useForm();
    const [isDirty, setIsDirty] = useState(false);
    const [strategy, setStrategy] = useState<"immediate" | "delayed">(rule.strategy);
    const [accountCondition, setAccountCondition] = useState<"all_accounts" | "specific_accounts">(rule.accountCondition);
    const [productCondition, setProductCondition] = useState<"all_products" | "specific_products">("specific_products");
    const [specificProductIds, setSpecificProductIds] = useState<string[]>([]);
    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const pageStyles = usePageStyles();
    const { token } = theme.useToken();
    const avatarPalette = useAvatarPalette();
    const tuners = useMemo(() => {
        return usersData?.data?.filter((user: any) =>
            user.role?.id === TunerRole.id || user.role?.id === 5
        ) || [];
    }, [usersData]);

    const rawRule = rule as unknown as Record<string, unknown>;
    const targetUserId = rule.targetUserId ?? (rawRule.targetUserId as number | undefined) ?? (rawRule.target_user_id as number | undefined);
    const targetGroupId = rule.targetGroupId ?? (rawRule.targetGroupId as number | undefined) ?? (rawRule.target_group_id as number | undefined);
    const targetServiceProviderId = rule.targetServiceProviderId ?? (rawRule.targetServiceProviderId as number | undefined) ?? (rawRule.target_service_provider_id as number | undefined);

    const assigneeDisplay = useMemo(() => {
        const uid = targetUserId != null ? Number(targetUserId) : undefined;
        const gid = targetGroupId != null ? Number(targetGroupId) : undefined;
        const pid = targetServiceProviderId != null ? Number(targetServiceProviderId) : undefined;
        if (uid != null && !Number.isNaN(uid)) {
            const user = tuners.find((u: any) => Number(u.id) === uid);
            if (user) {
                const name = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || `User ${user.id}`;
                return { type: "user" as const, name, initials: getInitials(user.firstName, user.lastName), color: getAvatarColor(Number(user.id), avatarPalette), memberCount: undefined, members: undefined };
            }
        }
        if (gid != null && !Number.isNaN(gid)) {
            const groupsList = Array.isArray(groupsData?.data) ? groupsData.data : (groupsData as any)?.data?.data;
            const group = (groupsList || []).find((g: any) => Number(g.id) === gid);
            if (group) {
                return { type: "group" as const, name: group.name, initials: getInitialsFromName(group.name), color: getAvatarColor(Number(group.id), avatarPalette), memberCount: group.users?.length ?? 0, members: group.users ?? [] };
            }
        }
        if (pid != null && !Number.isNaN(pid)) {
            const providersList = Array.isArray(serviceProvidersData?.data) ? serviceProvidersData.data : (serviceProvidersData as any)?.data?.data;
            const provider = (providersList || []).find((p: any) => Number(p.id) === pid);
            if (provider) {
                return { type: "provider" as const, name: provider.name, initials: getInitialsFromName(provider.name), color: getAvatarColor(Number(provider.id), avatarPalette), memberCount: undefined, members: undefined };
            }
        }
        return null;
    }, [targetUserId, targetGroupId, targetServiceProviderId, tuners, groupsData, serviceProvidersData, avatarPalette]);

    // Fetch accounts for multi-select
    const { data: accountsData } = useList({
        resource: "accounts",
        pagination: {
            mode: "off",
        },
    });

    // Fetch products for multi-select
    const { data: productsData, isLoading: productsLoading } = useList({
        resource: "products",
        pagination: {
            mode: "off",
        },
    });

    const accountOptions = accountsData?.data?.map((account: any) => ({
        label: account.name,
        value: String(account.id),
    })) || [];

    const { productSelectOptions, allProductIds } = useMemo(() => {
        const products = productsData?.data ?? [];
        if (!products.length) {
            return { productSelectOptions: [] as { label: string; options: { label: string; value: string }[] }[], allProductIds: [] as string[] };
        }
        type ProductLeaf = {
            value: string;
            label: string;
            name: string;
            type: string;
            group: string;
        };
        const buckets = new Map<string, { group: string; type: string; leaves: ProductLeaf[] }>();
        for (const product of products) {
            const group = typeof product.group === "string" ? product.group.trim() : "";
            const type = typeof product.type === "string" ? product.type : "";
            const name = product.name || String(product.id);
            const leaf: ProductLeaf = {
                value: String(product.id),
                label: name,
                name,
                type,
                group,
            };
            const key = `${group || UNGROUPED_KEY}||${type}`;
            const bucket = buckets.get(key);
            if (bucket) bucket.leaves.push(leaf);
            else buckets.set(key, { group, type, leaves: [leaf] });
        }
        const typeOrder = (type: string) => (type === "base" ? 0 : type === "option" ? 1 : 2);
        const groupLabel = (group: string) =>
            group || _t("service-providers.productMapping.ungrouped");
        const typeLabel = (type: string) =>
            type ? _t(`products.fields.typeValues.${type}`) : "";
        const productSelectOptions = [...buckets.values()]
            .sort((a, b) => {
                const ag = a.group || "￿";
                const bg = b.group || "￿";
                const groupCompare = ag.localeCompare(bg, undefined, { sensitivity: "base" });
                if (groupCompare !== 0) return groupCompare;
                return typeOrder(a.type) - typeOrder(b.type);
            })
            .map((bucket) => ({
                label: typeLabel(bucket.type)
                    ? `${groupLabel(bucket.group)} · ${typeLabel(bucket.type)}`
                    : groupLabel(bucket.group),
                options: bucket.leaves
                    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                    .map(({ value, label }) => ({ value, label })),
            }));
        const allProductIds = products.map((product: any) => String(product.id));
        return { productSelectOptions, allProductIds };
    }, [productsData, _t]);

    const isAllProductsSelected = (productIds: string[]): boolean => {
        if (allProductIds.length === 0 || productIds.length === 0) {
            return false;
        }
        if (productIds.length !== allProductIds.length) {
            return false;
        }
        const selectedIds = new Set(productIds);
        return allProductIds.every((id) => selectedIds.has(id));
    };

    const resolveProductCondition = (
        normalizedProductIds: string[],
    ): "all_products" | "specific_products" => {
        if (rule._isNew && normalizedProductIds.length === 0) {
            return "all_products";
        }
        if (isAllProductsSelected(normalizedProductIds)) {
            return "all_products";
        }
        return "specific_products";
    };

    const productsInitializedForRuleRef = useRef<string | null>(null);

    const getNormalizedProductIdsFromRule = (): string[] => {
        if (rule.products && Array.isArray(rule.products)) {
            return rule.products
                .map((product: any) => String(product.id))
                .filter(Boolean);
        }
        if (rule.productIds) {
            return rule.productIds
                .map((id: string | number) => String(id))
                .filter(Boolean);
        }
        return [];
    };

    const applyProductStateFromRule = (normalizedProductIds: string[]) => {
        const resolvedProductCondition = resolveProductCondition(normalizedProductIds);
        const resolvedSpecificProductIds =
            resolvedProductCondition === "all_products" ? [] : normalizedProductIds;
        const resolvedProductIds =
            resolvedProductCondition === "all_products" && allProductIds.length > 0
                ? allProductIds
                : normalizedProductIds;
        setProductCondition(resolvedProductCondition);
        setSpecificProductIds(resolvedSpecificProductIds);
        form.setFieldsValue({ productIds: resolvedProductIds });
    };

    useEffect(() => {
        let normalizedAccountIds: string[] = [];
        if (rule.accounts && Array.isArray(rule.accounts)) {
            normalizedAccountIds = rule.accounts
                .map((account: any) => String(account.id))
                .filter(Boolean);
        } else if (rule.accountIds) {
            normalizedAccountIds = rule.accountIds
                .map((id: string | number) => String(id))
                .filter(Boolean);
        }
        const isActiveValue = rule.isActive !== undefined && rule.isActive !== null ? Boolean(rule.isActive) : true;
        const ruleName = `Rule #${rule.id}`;
        form.setFieldsValue({
            name: ruleName,
            description: rule.description || null,
            assignmentType: rule.assignmentType,
            targetUserId: rule.targetUserId || undefined,
            targetServiceProviderId: rule.targetServiceProviderId || undefined,
            targetGroupId: rule.targetGroupId || undefined,
            strategy: rule.strategy,
            delayMinutes: rule.delayMinutes || undefined,
            priority: rule.priority,
            isActive: isActiveValue,
            accountCondition: rule.accountCondition,
            accountIds: normalizedAccountIds,
        });
        setStrategy(rule.strategy);
        setAccountCondition(rule.accountCondition);
        setIsDirty(rule._isNew === true);
    }, [rule, form]);

    useEffect(() => {
        if (allProductIds.length === 0) {
            return;
        }
        const ruleKey = String(rule.id);
        if (productsInitializedForRuleRef.current === ruleKey) {
            return;
        }
        productsInitializedForRuleRef.current = ruleKey;
        applyProductStateFromRule(getNormalizedProductIdsFromRule());
    }, [rule.id, allProductIds, form]);

    const handleValuesChange = (changedValues: any, allValues: any) => {
        setIsDirty(true);
        if ("productIds" in changedValues && productCondition === "specific_products") {
            setSpecificProductIds((changedValues.productIds ?? []).map(String));
        }
        if (onLocalUpdate && rule._isNew) {
            onLocalUpdate(rule.id, allValues);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsDirty(true);
        const allValues = form.getFieldsValue();
        if (onLocalUpdate && rule._isNew) {
            onLocalUpdate(rule.id, { ...allValues, [e.target.name || 'name']: e.target.value });
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const hasAssignee = values.targetUserId || values.targetGroupId || values.targetServiceProviderId;
            if (!hasAssignee) {
                message.error(_t("assignment-rules.fields.assigneeRequired", "Please select an assignee (user, group or service provider)"));
                return;
            }
            const assignmentType = values.targetUserId ? "user" : values.targetGroupId ? "group" : "service_provider";
            const resolvedProductIds =
                productCondition === "all_products"
                    ? allProductIds
                    : (values.productIds ?? []).map(String);
            const updateData: any = {
                name: values.name,
                description: values.description || null,
                assignmentType,
                strategy: values.strategy,
                targetUserId: values.targetUserId || null,
                targetServiceProviderId: values.targetServiceProviderId || null,
                targetGroupId: values.targetGroupId || null,
                delayMinutes: values.delayMinutes || null,
                priority: values.priority,
                isActive: values.isActive,
                accountCondition: values.accountCondition,
                productIds: resolvedProductIds.map((id: string) => Number(id)),
                accountIds: values.accountIds?.map((id: string | number) => Number(id)) || [],
            };
            if (values.accountCondition === "all_accounts") {
                delete updateData.accountIds;
            }
            onUpdate(rule.id, updateData);
            setIsDirty(false);
        } catch (error) {
            console.error("Validation failed:", error);
            if (error && typeof error === "object" && "errorFields" in error) {
                const errorFields = (error as any).errorFields;
                if (errorFields?.length > 0) {
                    console.error("Validation errors:", errorFields);
                }
            }
        }
    };

    const handleAssignForRule = (type: "tuner" | "group" | "provider", targetId: number) => {
        const assignmentType: "user" | "group" | "service_provider" = type === "tuner" ? "user" : type === "group" ? "group" : "service_provider";
        const formPayload = {
            assignmentType,
            targetUserId: type === "tuner" ? targetId : undefined,
            targetGroupId: type === "group" ? targetId : undefined,
            targetServiceProviderId: type === "provider" ? targetId : undefined,
        };
        form.setFieldsValue(formPayload);
        setIsDirty(true);
        if (rule._isNew && onLocalUpdate) {
            onLocalUpdate(rule.id, {
                ...rule,
                assignmentType,
                targetUserId: type === "tuner" ? targetId : undefined,
                targetGroupId: type === "group" ? targetId : undefined,
                targetServiceProviderId: type === "provider" ? targetId : undefined,
            });
        } else {
            onUpdate(rule.id, {
                assignmentType,
                targetUserId: type === "tuner" ? targetId : undefined,
                targetGroupId: type === "group" ? targetId : undefined,
                targetServiceProviderId: type === "provider" ? targetId : undefined,
            });
        }
        setAssignmentModalOpen(false);
    };

    const handleStrategyChange = (value: "immediate" | "delayed") => {
        setStrategy(value);
        if (value === "immediate") {
            form.setFieldsValue({ delayMinutes: undefined });
        }
        setIsDirty(true);
    };

    const handleAccountConditionChange = (value: "all_accounts" | "specific_accounts") => {
        setAccountCondition(value);
        if (value === "all_accounts") {
            form.setFieldsValue({ accountIds: undefined });
        }
        setIsDirty(true);
    };

    const handleProductConditionChange = (value: "all_products" | "specific_products") => {
        setProductCondition(value);
        let nextProductIds: string[];
        if (value === "all_products") {
            const currentProductIds = (form.getFieldValue("productIds") ?? []).map(String);
            if (currentProductIds.length > 0 && !isAllProductsSelected(currentProductIds)) {
                setSpecificProductIds(currentProductIds);
            }
            nextProductIds = allProductIds;
        } else {
            nextProductIds = specificProductIds;
        }
        form.setFieldsValue({ productIds: nextProductIds });
        setIsDirty(true);
        if (onLocalUpdate && rule._isNew) {
            onLocalUpdate(rule.id, {
                ...form.getFieldsValue(),
                productIds: nextProductIds,
            });
        }
    };

    const handleBackReset = () => {
        let normalizedProductIds = getNormalizedProductIdsFromRule();
        let normalizedAccountIds: string[] = [];
        if (rule.accounts && Array.isArray(rule.accounts)) {
            normalizedAccountIds = rule.accounts.map((a: any) => String(a.id)).filter(Boolean);
        } else if (rule.accountIds) {
            normalizedAccountIds = rule.accountIds.map((id: string | number) => String(id)).filter(Boolean);
        }
        const resolvedProductCondition = resolveProductCondition(normalizedProductIds);
        const resolvedSpecificProductIds =
            resolvedProductCondition === "all_products" ? [] : normalizedProductIds;
        const resolvedProductIds =
            resolvedProductCondition === "all_products" && allProductIds.length > 0
                ? allProductIds
                : normalizedProductIds;
        const isActiveValue = rule.isActive !== undefined && rule.isActive !== null ? Boolean(rule.isActive) : true;
        form.setFieldsValue({
            name: `Rule #${rule.id}`,
            description: rule.description || null,
            assignmentType: rule.assignmentType,
            targetUserId: rule.targetUserId || undefined,
            targetServiceProviderId: rule.targetServiceProviderId || undefined,
            targetGroupId: rule.targetGroupId || undefined,
            strategy: rule.strategy,
            delayMinutes: rule.delayMinutes || undefined,
            priority: rule.priority,
            isActive: isActiveValue,
            accountCondition: rule.accountCondition,
            productIds: resolvedProductIds,
            accountIds: normalizedAccountIds,
        });
        setProductCondition(resolvedProductCondition);
        setSpecificProductIds(resolvedSpecificProductIds);
        setStrategy(rule.strategy);
        setAccountCondition(rule.accountCondition);
        setIsDirty(false);
    };

    const cardActions = [
        <Flex key="actions" align="center" justify="space-between"
            style={{ width: "100%" }} gap="middle"
            className="px-6">
            <Button
                key="back"
                icon={<UndoOutlined />}
                onClick={handleBackReset}
            >
                {_t("buttons.reset", "Reset")}
            </Button>
            <Space size="small">
                <Space.Compact size="small" direction="horizontal">
                    <Button
                        key="up"
                        size="large"
                        icon={<ArrowUpOutlined />}
                        onClick={() => onMoveUp(index)}
                        disabled={index === 0}
                        title={_t("assignment-rules.actions.moveUp", "Move Up")}
                    />
                    <Button size="large" disabled>
                    <span className="text-inherit opacity-90">{_t("assignment-rules.fields.priority", "Priority")}:</span>
                    </Button>
                    <Button
                        key="down"
                        size="large"
                        icon={<ArrowDownOutlined />}
                        onClick={() => onMoveDown(index)}
                        disabled={index === totalRules - 1}
                        title={_t("assignment-rules.actions.moveDown", "Move Down")}
                    />

                </Space.Compact>
            </Space>
            <Space size="small">
                <Button
                    key="delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDelete(rule.id)}
                >
                    {_t("buttons.delete", "Delete")}
                </Button>
                <Button
                    key="save"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!isDirty}
                >
                    {_t("buttons.save", "Save")}
                </Button>
            </Space>
        </Flex>,
    ];

    return (
        <Form
            key={rule.id} // Force re-render when rule changes
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
        >
            <Card
                style={pageStyles.stepperCard}
                styles={{ header: { background: "transparent", borderBottom: "none" }, actions: { background: "transparent", borderTop: "none" } }}
                title={
                    <Row gutter={[8, 8]} align="middle">
                        <Col xs={24} sm={18} md={20}>
                            <Form.Item name="name" hidden rules={[{ required: true, message: _t("fields.errors.required") }]}>
                                <Input />
                            </Form.Item>
                            <Typography.Text strong className="block w-full">
                                {_t("assignment-rules.ruleNameDefault", { id: rule.id }, `Rule #${rule.id}`)}
                            </Typography.Text>
                        </Col>
                        <Col xs={24} sm={6} md={4} style={{ textAlign: "right" }}>
                            <Space>
                                <label htmlFor="isActive" className="mr-2">{_t("assignment-rules.fields.isActive", "Active")}</label>
                                <Form.Item
                                    name="isActive"
                                    noStyle
                                    valuePropName="checked"
                                    getValueFromEvent={(checked) => checked}
                                >
                                    <Switch
                                        id="isActive"
                                        checkedChildren={_t("titles.yes", "Active")}
                                        unCheckedChildren={_t("titles.no", "Inactive")}
                                    />
                                </Form.Item>
                            </Space>
                        </Col>
                    </Row>
                }
                actions={cardActions}
            >
                <Form.Item
                    name="description"
                    hidden={true}
                    label={_t("assignment-rules.fields.description", "Description")}
                >
                    <TextArea
                        rows={2}
                        placeholder={_t("assignment-rules.fields.description", "Description")}
                    />
                </Form.Item>

                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                        <Card style={pageStyles.stepperCard} className="h-full">
                            <Flex gap={8} align="center" className="mb-4">
                                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                                <span className="uppercase text-md font-bold">
                                    {_t("assignment-rules.fields.conditions", "Conditions")}
                                </span>
                            </Flex>
                            <Form.Item
                                label={_t("assignment-rules.fields.productIds", "Products")}
                                required
                            >
                                {productsLoading ? (
                                    <Select loading disabled className="w-full" placeholder={_t("titles.refreshing", "Loading...")} />
                                ) : (
                                    <Radio.Group
                                        value={productCondition}
                                        onChange={(event) => handleProductConditionChange(event.target.value)}
                                        className="flex w-full"
                                    >
                                        <Radio.Button value="all_products" className="flex-1 text-center">
                                            {_t("assignment-rules.fields.productConditionValues.all_products", "All products")}
                                        </Radio.Button>
                                        <Radio.Button value="specific_products" className="flex-1 text-center">
                                            {_t("assignment-rules.fields.productConditionValues.specific_products", "Specific products")}
                                        </Radio.Button>
                                    </Radio.Group>
                                )}
                            </Form.Item>

                            {!productsLoading && productCondition === "all_products" ? (
                                <>
                                    <Form.Item name="productIds" hidden rules={[{ required: true, message: _t("fields.errors.required") }]}>
                                        <Select mode="multiple" options={[]} />
                                    </Form.Item>
                                    {allProductIds.length > 0 && (
                                        <Typography.Text type="secondary" className="mb-4 block text-sm">
                                            {_t(
                                                "assignment-rules.fields.allProductsHint",
                                                { count: allProductIds.length },
                                                `${allProductIds.length} products included`,
                                            )}
                                        </Typography.Text>
                                    )}
                                </>
                            ) : !productsLoading ? (
                                <Form.Item
                                    name="productIds"
                                    label={_t("assignment-rules.fields.specificProductIds", "Select products")}
                                    rules={[{ required: true, message: _t("fields.errors.required") }]}
                                >
                                    <Select
                                        mode="multiple"
                                        options={productSelectOptions}
                                        placeholder={_t("assignment-rules.fields.specificProductIds", "Select products")}
                                        className="w-full"
                                        maxTagCount="responsive"
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            ) : null}

                            <Form.Item
                                name="accountCondition"
                                label={_t("assignment-rules.fields.accountCondition", "Account Condition")}
                                rules={[{ required: true, message: _t("fields.errors.required") }]}
                            >
                                <Radio.Group
                                    onChange={(e) => handleAccountConditionChange(e.target.value)}
                                    style={{ width: "100%" }}
                                >
                                    <Radio.Button value="all_accounts" style={{ flex: 1, textAlign: "center" }}>
                                        {_t("assignment-rules.fields.accountConditionValues.all_accounts", "All Accounts")}
                                    </Radio.Button>
                                    <Radio.Button value="specific_accounts" style={{ flex: 1, textAlign: "center" }}>
                                        {_t("assignment-rules.fields.accountConditionValues.specific_accounts", "Specific Accounts")}
                                    </Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {accountCondition === "specific_accounts" && (
                                <Form.Item
                                    name="accountIds"
                                    label={_t("assignment-rules.fields.accountIds", "Accounts")}
                                    rules={[{ required: true, message: _t("fields.errors.required") }]}
                                >
                                    <Select
                                        mode="multiple"
                                        options={accountOptions}
                                        placeholder={_t("assignment-rules.fields.accountIds", "Select Accounts")}
                                        style={{ width: "100%" }}
                                        maxTagCount="responsive"
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            )}
                        </Card>

                    </Col>

                    <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                        <Card style={pageStyles.stepperCard} className="h-full">
                            <Flex gap={8} align="center" className="mb-4">
                                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                                <span className="uppercase text-md font-bold">
                                    {_t("assignment-rules.fields.effects", "Effets")}
                                </span>
                            </Flex>

                            <Form.Item
                                label={_t("assignment-rules.fields.assignmentType", "Assign to")}
                                required
                            >
                                <Flex gap={12} align="center" wrap="wrap">
                                    {assigneeDisplay ? (
                                        <>
                                            <Card size="small" className="flex-1 min-w-0" styles={{ body: { padding: 12 } }}>
                                                <Flex gap={12} align="start">
                                                    <Avatar
                                                        size={40}
                                                        shape="square"
                                                        style={{ backgroundColor: assigneeDisplay.color, fontWeight: 600 }}
                                                    >
                                                        {assigneeDisplay.initials}
                                                    </Avatar>
                                                    <Flex vertical gap={0} className="min-w-0 flex-1">
                                                        <Typography.Text strong className="block truncate">{assigneeDisplay.name}</Typography.Text>
                                                        {assigneeDisplay.type === "group" && assigneeDisplay.memberCount !== undefined && (
                                                            <Flex gap={4} align="center" className="mt-1">
                                                                <Avatar.Group size="small" max={{ count: 3 }}>
                                                                    {(assigneeDisplay.members ?? []).slice(0, 3).map((user: any, i: number) => (
                                                                        <Tooltip key={i} title={`${user.firstName || ""} ${user.lastName || ""}`.trim()}>
                                                                            <Avatar size="small" shape="square" style={{ backgroundColor: getAvatarColor(user.id, avatarPalette) }}>
                                                                                {getInitials(user.firstName, user.lastName)}
                                                                            </Avatar>
                                                                        </Tooltip>
                                                                    ))}
                                                                </Avatar.Group>
                                                                <Typography.Text type="secondary" className="text-xs">
                                                                    {assigneeDisplay.memberCount} {assigneeDisplay.memberCount === 1 ? _t("user-groups.fields.users")?.slice(0, -1) : _t("user-groups.fields.users")}
                                                                </Typography.Text>
                                                            </Flex>
                                                        )}
                                                    </Flex>
                                                    <Button
                                                        type="text"
                                                        icon={<EditOutlined />}
                                                        onClick={() => setAssignmentModalOpen(true)}
                                                        className="shrink-0"
                                                        title={_t("assignment-rules.fields.changeAssignee", "Change")}
                                                    />
                                                </Flex>
                                            </Card>
                                        </>
                                    ) : (
                                        <Button
                                            type="default"
                                            icon={<UserOutlined />}
                                            onClick={() => setAssignmentModalOpen(true)}
                                        >
                                            {_t("assignment-rules.fields.selectAssignee", "Select user, group or service provider")}
                                        </Button>
                                    )}
                                </Flex>
                            </Form.Item>

                            <Flex gap={16}>
                                <Form.Item
                                    name="strategy"
                                    label={_t("assignment-rules.fields.strategy", "Strategy")}
                                    rules={[{ required: true, message: _t("fields.errors.required") }]}
                                >
                                    <Radio.Group
                                        onChange={(e) => handleStrategyChange(e.target.value)}
                                        style={{ width: "100%" }}
                                    >
                                        <Radio.Button value="immediate" style={{ flex: 1, textAlign: "center" }}>
                                            {_t("assignment-rules.fields.strategyValues.immediate", "Immediate")}
                                        </Radio.Button>
                                        <Radio.Button value="delayed" style={{ flex: 1, textAlign: "center" }}>
                                            {_t("assignment-rules.fields.strategyValues.delayed", "Delayed")}
                                        </Radio.Button>
                                    </Radio.Group>
                                </Form.Item>

                                {strategy === "delayed" && (
                                    <Form.Item
                                        name="delayMinutes"
                                        label={_t("assignment-rules.fields.delayMinutes", "Delay (minutes)")}
                                        rules={[{ required: true, message: _t("fields.errors.required") }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            style={{ width: "100%" }}
                                            placeholder={_t("assignment-rules.fields.delayMinutes", "Delay in minutes")}
                                        />
                                    </Form.Item>
                                )}
                            </Flex>
                        </Card>
                    </Col>
                </Row>

                <Form.Item name="assignmentType" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item name="targetUserId" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item name="targetGroupId" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item name="targetServiceProviderId" hidden>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item name="priority" hidden>
                    <Input type="hidden" />
                </Form.Item>
            </Card>

            <AssignmentModal
                open={assignmentModalOpen}
                onCancel={() => setAssignmentModalOpen(false)}
                assignedUserId={targetUserId != null && !Number.isNaN(Number(targetUserId)) ? Number(targetUserId) : undefined}
                assignedServiceProviderId={targetServiceProviderId != null && !Number.isNaN(Number(targetServiceProviderId)) ? Number(targetServiceProviderId) : undefined}
                assignedGroupId={targetGroupId != null && !Number.isNaN(Number(targetGroupId)) ? Number(targetGroupId) : undefined}
                usersData={usersData}
                serviceProvidersData={serviceProvidersData}
                groupsData={groupsData}
                mode="rule"
                onAssignForRule={handleAssignForRule}
                isAdmin={true}
                _t={_t}
            />
        </Form >
    );
};

